import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useMemo, useState } from 'react';
import { api, ApiError, type WireLine } from '@/api/client';
import { Histogram } from '@/components/Histogram';
import { Icon } from '@/components/Icon';
import { Stack } from '@/components/Inspector';
import { ago, compactCount, envStyle, formatCount, LEVEL_LABEL, lineTime, messageText, shortStamp } from '@/lib/format';
import { newRuleLink } from '@/lib/alerts';
import { useNow } from '@/lib/hooks';
import { ISSUE_RANGES, STATUS_LABEL } from '@/lib/issues';
import { Link } from '@/lib/router';
import { useSettings } from '@/lib/settings';
import { usePageCommands, useShell } from '@/lib/shell';
import { renderText } from '@/lib/text';

const BITBUCKET = 'https://bitbucket.org/adoreasrl/bustard/src';
const isObject = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null && !Array.isArray(value);

function readRange(): string {
    const range = new URLSearchParams(location.search).get('range') ?? '24h';

    return ISSUE_RANGES.some(([key]) => key === range) ? range : '24h';
}

// One issue whole: what it is, how it goes, where it happens, to whom, since which version; its latest stack; its
// occurrences, each one click from EXPLORE.
export function IssuePage({ fingerprint }: { fingerprint: string }): ReactNode {
    const { env, privacy, say, openLine } = useShell();
    const { timeZone: zone } = useSettings();
    const queryClient = useQueryClient();
    const [range, setRange] = useState(readRange);
    const [anchor] = useState(() => Date.now());
    const now = useNow(15_000);
    const span = ISSUE_RANGES.find(([key]) => key === range)?.[2] ?? 24 * 3600_000;
    const scope = useMemo(() => ({ env, q: '', from: anchor - span, to: anchor }), [env, anchor, span]);
    const narrowed = useMemo(() => ({ ...scope, q: `fingerprint:${fingerprint}` }), [scope, fingerprint]);

    const detail = useQuery({
        queryKey: ['issue', fingerprint, scope],
        queryFn: ({ signal }) => api.issue(fingerprint, scope, signal),
        enabled: env !== '',
        placeholderData: (previous) => previous,
    });
    const issue = detail.data?.issue ?? null;
    const latest = useQuery({
        queryKey: ['line', env, issue?.lastId],
        queryFn: () => api.line(env, issue?.lastId as string),
        enabled: Boolean(issue?.lastId),
        staleTime: Infinity,
    });
    const histogram = useQuery({
        queryKey: ['histogram', narrowed],
        queryFn: ({ signal }) => api.histogram(narrowed, signal),
        enabled: env !== '',
    });
    const occurrences = useQuery({
        queryKey: ['occurrences', narrowed],
        queryFn: ({ signal }) => api.search(narrowed, null, signal),
        enabled: env !== '',
    });
    const setState = useMutation({
        mutationFn: (state: { status: 'open' | 'acknowledged' | 'resolved' | 'muted'; mutedHours?: number | null }) =>
            api.setIssueState(fingerprint, { env, status: state.status, mutedHours: state.mutedHours ?? null }),
        onSuccess: async (_, state) => {
            say(state.status === 'open' ? 'Reopened' : `Marked ${state.status}`);
            await queryClient.invalidateQueries({ queryKey: ['issue', fingerprint] });
            await queryClient.invalidateQueries({ queryKey: ['issues'] });
        },
        onError: (error) => say(error instanceof ApiError ? error.message : 'Not saved'),
    });
    const exploreLink = `/?env=${env}&q=${encodeURIComponent(`fingerprint:${fingerprint}`)}&from=${scope.from}&to=${scope.to}`;
    const attributes = (latest.data?.line.attributes ?? {}) as Record<string, unknown>;
    const exception = isObject(attributes.exception) ? attributes.exception : null;

    usePageCommands([
        { icon: '✓', label: 'Resolve this issue', run: () => setState.mutate({ status: 'resolved' }) },
        { icon: '◐', label: 'Acknowledge this issue', run: () => setState.mutate({ status: 'acknowledged' }) },
        { icon: '◌', label: 'Mute this issue for 24 hours', run: () => setState.mutate({ status: 'muted', mutedHours: 24 }) },
    ]);

    if (detail.isLoading) {
        return <div className="page page-scroll">{/* first load */}</div>;
    }

    return (
        <div className="page page-scroll">
            <div className="crumbs">
                <Link to={`/issues?env=${env}&range=${range}`}>
                    <Icon name="back" size={15} /> Issues
                </Link>
                <span>· {envStyle(env).label}</span>
            </div>
            {!issue ? (
                <div className="panel placeholder">
                    <h2>Not in this range</h2>
                    <p>
                        No occurrence of this issue in {env} in the last {ISSUE_RANGES.find(([key]) => key === range)?.[1].toLowerCase()}.
                    </p>
                    <div className="seg">
                        {ISSUE_RANGES.map(([key, label]) => (
                            <button key={key} className={key === range ? 'on' : ''} onClick={() => setRange(key)}>
                                {label}
                            </button>
                        ))}
                    </div>
                </div>
            ) : (
                <>
                    <div className="panel focus issue-head">
                        <div className="ih-top">
                            <span className={`status ${issue.status}`}>{STATUS_LABEL[issue.status]}</span>
                            {issue.isNew && <span className="newbadge">New</span>}
                            <span className={`lv ${issue.level}`}>{LEVEL_LABEL[issue.level]}</span>
                            <span className="micro">
                                {issue.module} · {issue.logger}
                            </span>
                            <span className="spacer" />
                            <div className="seg">
                                {ISSUE_RANGES.map(([key, label]) => (
                                    <button key={key} className={key === range ? 'on' : ''} onClick={() => setRange(key)}>
                                        {label}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <h2>{issue.title}</h2>
                        <div className="issue-template big">{issue.template}</div>
                        <div className="ih-actions">
                            {(issue.status === 'open' || issue.status === 'regressed') && (
                                <button className="act" onClick={() => setState.mutate({ status: 'acknowledged' })}>
                                    Acknowledge
                                </button>
                            )}
                            {issue.status !== 'resolved' && (
                                <button className="act primary" onClick={() => setState.mutate({ status: 'resolved' })}>
                                    Resolve
                                </button>
                            )}
                            {issue.status !== 'muted' && (
                                <>
                                    <button className="act" onClick={() => setState.mutate({ status: 'muted', mutedHours: 24 })}>
                                        Mute 24h
                                    </button>
                                    <button className="act" onClick={() => setState.mutate({ status: 'muted', mutedHours: null })}>
                                        Mute for good
                                    </button>
                                </>
                            )}
                            {issue.status !== 'open' && issue.status !== 'regressed' && (
                                <button className="act" onClick={() => setState.mutate({ status: 'open' })}>
                                    Reopen
                                </button>
                            )}
                            <span className="spacer" />
                            <Link
                                className="act"
                                to={newRuleLink({
                                    env,
                                    q: `fingerprint:${fingerprint}`,
                                    name: `${issue.title} again`,
                                    kind: 'threshold',
                                    threshold: 0,
                                })}
                                title="A rule that fires whenever this error happens"
                            >
                                {issue.status === 'resolved' || issue.status === 'muted' ? 'Alert if it comes back' : 'Alert on it'}
                            </Link>
                            <Link className="act" to={exploreLink}>
                                Open in Explore →
                            </Link>
                        </div>
                    </div>
                    <div className="tiles">
                        <div className="tile">
                            <span className="micro">Events</span>
                            <b>{formatCount(issue.count)}</b>
                        </div>
                        <div className="tile">
                            <span className="micro">Users hit</span>
                            <b>{formatCount(issue.users)}</b>
                        </div>
                        <div className="tile">
                            <span className="micro">Instances</span>
                            <b>{formatCount(detail.data?.instances ?? 0)}</b>
                        </div>
                        <div className="tile">
                            <span className="micro">First seen</span>
                            <b>{ago(issue.firstEver, now)} ago</b>
                            <span className="mono small">
                                {shortStamp(issue.firstEver, zone)} · {issue.firstEverCommit.slice(0, 7) || 'no commit'}
                            </span>
                        </div>
                        <div className="tile">
                            <span className="micro">Last seen</span>
                            <b>{ago(issue.lastSeen, now)} ago</b>
                            <span className="mono small">
                                {shortStamp(issue.lastSeen, zone)} · {issue.lastCommit.slice(0, 7) || 'no commit'}
                            </span>
                        </div>
                    </div>
                    <div className="panel">
                        <div className="panel-head">
                            <span className="micro">
                                OCCURRENCES <b>· LAST {ISSUE_RANGES.find(([key]) => key === range)?.[1]}</b>
                            </span>
                        </div>
                        <Histogram data={histogram.data} from={scope.from} to={scope.to} zone={zone} requestsHidden={false} onZoom={null} />
                    </div>
                    <div className="issue-cols">
                        <div className="panel">
                            <div className="panel-head">
                                <span className="title">Stack</span>
                                <span className="note">the latest occurrence</span>
                                <span className="spacer" />
                                {latest.data && (
                                    <button className="act" onClick={() => openLine({ env, id: issue.lastId })}>
                                        Open the line
                                    </button>
                                )}
                            </div>
                            <div className="ins-body issue-stack">
                                {exception ? (
                                    <Stack exception={exception} commit={latest.data?.line.commit ?? ''} />
                                ) : latest.isLoading ? (
                                    <span className="micro">Loading…</span>
                                ) : (
                                    <div className="ins-msg">{renderText(latest.data?.line.message ?? issue.message, { privacy })}</div>
                                )}
                            </div>
                        </div>
                        <div className="issue-side">
                            <div className="panel">
                                <div className="panel-head">
                                    <span className="title">Where</span>
                                    <span className="note">routes</span>
                                </div>
                                <Bars
                                    rows={(detail.data?.routes ?? []).map((route) => ({
                                        key: `${route.method} ${route.path}`,
                                        label: `${route.method} ${route.path}`,
                                        count: route.count,
                                    }))}
                                    empty="Not in an HTTP request"
                                />
                            </div>
                            <div className="panel">
                                <div className="panel-head">
                                    <span className="title">Who</span>
                                    <span className="note">users hit most</span>
                                </div>
                                <Bars
                                    rows={(detail.data?.users ?? []).map((user) => ({
                                        key: user.user,
                                        label: user.user,
                                        count: user.count,
                                        secret: true,
                                    }))}
                                    privacy={privacy}
                                    empty="No signed-in user"
                                />
                            </div>
                            <div className="panel">
                                <div className="panel-head">
                                    <span className="title">When</span>
                                    <span className="note">by version</span>
                                </div>
                                <div className="versions">
                                    {(detail.data?.commits ?? []).map((commit) => (
                                        <div key={commit.commit} className="version">
                                            {commit.commit ? (
                                                <a className="link mono" href={`${BITBUCKET}/${commit.commit}`} target="_blank" rel="noreferrer">
                                                    {commit.commit.slice(0, 9)} ↗
                                                </a>
                                            ) : (
                                                <span className="mono">no commit</span>
                                            )}
                                            <span className="mono small">
                                                {compactCount(commit.count)} · {shortStamp(commit.firstMs, zone)} → {shortStamp(commit.lastMs, zone)}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="panel">
                        <div className="panel-head">
                            <span className="title">Latest occurrences</span>
                            <span className="note">click one to open it in Explore</span>
                            <span className="spacer" />
                            <Link className="act" to={exploreLink}>
                                All of them →
                            </Link>
                        </div>
                        <div className="occurrences">
                            {(occurrences.data?.lines ?? []).slice(0, 25).map((line: WireLine) => {
                                const time = lineTime(line.ts, zone);

                                return (
                                    <button key={line.id} className="ctx-row lv-error" onClick={() => openLine({ env, id: line.id })}>
                                        <span className="t">
                                            {time.clock}
                                            <span className="ms">{time.ms}</span>
                                        </span>
                                        <span className="lv error">{LEVEL_LABEL[line.level] ?? line.level}</span>
                                        <span className="m">
                                            {renderText(messageText(line.message), { privacy, secrets: line.user_id ? [line.user_id] : [] })}
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}

// A ranked list with its bar: routes, users.
function Bars(props: { rows: Array<{ key: string; label: string; count: number; secret?: boolean }>; privacy?: boolean; empty: string }): ReactNode {
    const max = Math.max(1, ...props.rows.map((row) => row.count));
    if (props.rows.length === 0) {
        return <div className="bars-empty micro">{props.empty}</div>;
    }

    return (
        <div className="bars">
            {props.rows.map((row) => (
                <div key={row.key} className="bar-row">
                    <span className="bar-label mono" title={row.secret && props.privacy ? undefined : row.label}>
                        {row.secret && props.privacy ? <span className="redact">████████████</span> : row.label}
                    </span>
                    <span className="meter">
                        <b style={{ width: `${Math.max(3, (row.count / max) * 100)}%` }} />
                    </span>
                    <span className="mono small">{compactCount(row.count)}</span>
                </div>
            ))}
        </div>
    );
}
